import { Link } from "@tanstack/react-router";
import { Alert, Card, Col, Row, Segmented, Skeleton, Statistic } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEmployeeAttendanceDays } from "../hooks/useEmployees";
import type { EmployeeDetail } from "../types";
import { soatDaqiqa } from "../utils/formatMinutes";
import { toLateSeries } from "../utils/lateSeries";
import { recentDates, summarise, toDayCells } from "../utils/summary";
import { DayStrip, DayStripLegend } from "./DayStrip";
import { EmployeeZonesCard } from "./EmployeeZonesCard";
import { LateChartSlot } from "./LateChartSlot";
import { RecentAbsencesCard } from "./RecentAbsencesCard";
import { RecentDaysTable } from "./RecentDaysTable";
import { RiskCard } from "./RiskCard";

/**
 * Lenta oynasi — dizayn kanvasidagi segmentli tanlagich.
 *
 * ⚠️⚠️ **90 — MAKSIMUM va u `useEmployeeAttendanceDays` ning `limit: 100`
 * i bilan BOG'LANGAN** (`useEmployees.ts`; `AttendanceDayListQuerySchema`
 * ning `limit` maksimumi ham 100). `attendance_days` da
 * `(employee_id, work_date)` UNIQUE, ya'ni 90 kunlik oyna eng ko'pi bilan
 * 90 qator beradi va bitta sahifaga sig'adi. Chegaraga yaqinlik ONGLI:
 * oynani kattalashtirish javobni JIMGINA kesardi — xato bo'lmaydi,
 * shunchaki kunlar yo'qoladi va foiz, o'rtacha hamda diagramma noto'g'ri
 * chiqadi. Kattaroq oyna avval sahifalashni talab qiladi.
 *
 * ⚠️ **Kanvasdagi «Yil» varianti ATAYLAB YO'Q**: (a) hech bir endpoint
 * 365 kunni bermaydi, (b) `DayStrip` 365 katakni ~2 px kenglikda chizib,
 * tooltip nishonini yo'qotardi.
 */
const WINDOW_OPTIONS = [30, 90] as const;
type WindowDays = (typeof WINDOW_OPTIONS)[number];

/**
 * Bugungi sana `YYYY-MM-DD` shaklida.
 *
 * ⚠️ Zona — BRAUZERNIKI, `settings.timezone` EMAS. Bu ONGLI
 * soddalashtirish: panel bir shaharda ishlatiladi va oynaning bir kun
 * siljishi lentaning ma'nosini o'zgartirmaydi. Zona sozlamasi kerak
 * bo'lganda `GET /settings` dan olinadi — bu qarz shu izohda qoladi.
 */
function bugun(): string {
	const now = new Date();

	return [
		now.getFullYear(),
		String(now.getMonth() + 1).padStart(2, "0"),
		String(now.getDate()).padStart(2, "0"),
	].join("-");
}

/**
 * «Umumiy» yorlig'i — kanvasdagi ikki ustunli tartib.
 *
 * ⚠️ **«Topshiriqlar» kartasi ATAYLAB yo'q** — kanvasda to'rtinchi
 * ko'rsatkich sifatida turadi, lekin u `GET /tasks?employeeId=` ning
 * agregatini talab qiladi va u endpoint hali yo'q. Bo'sh yoki soxta
 * qiymatli karta ko'rsatilmaydi.
 *
 * ⚠️⚠️ **`Row`/`Col` karkasi FAQAT shu yerda quriladi.** Chap ustun
 * `lg=16` (ko'rsatkichlar, lenta, diagramma, jadval), o'ng `lg=8`
 * (xavf, hududlar, arizalar). Ikkinchi karkas qurish ustunlarning
 * kengligini jimgina ajratardi.
 */
export function EmployeeOverviewTab({ employee }: { employee: EmployeeDetail }) {
	const { t } = useTranslation("employees");
	const employeeId = employee.id;

	// ⚠️ Oyna `?window=` bilan URL'ga YOZILMAYDI — ONGLI cheklov: yorliq
	// allaqachon `?tab=` da va ikkinchi qidiruv parametri `replace: true`
	// mantiqini murakkablashtirardi.
	const [oynaKun, setOynaKun] = useState<WindowDays>(30);
	const oyna = useMemo(() => recentDates(oynaKun, bugun()), [oynaKun]);
	const from = oyna[0];
	const to = oyna[oyna.length - 1];

	const { data: days, isLoading, isError } = useEmployeeAttendanceDays(employeeId, from, to);

	// ⚠️ `summarise` FAQAT haqiqiy qatorlarni oladi, `toDayCells` esa
	// oynani ham — to'ldirilgan kun maxrajga kirsa foiz jimgina pasayardi.
	const cells = useMemo(() => toDayCells(days ?? [], oyna), [days, oyna]);
	const korsatkich = useMemo(() => summarise(days ?? []), [days]);

	// ⚠️ Seriya `cells` bilan AYNI oynadan quriladi — diagramma va lenta
	// bir xil kunlarni ko'rsatishi shart, aks holda ular jimgina ajralardi.
	const series = useMemo(() => toLateSeries(days ?? [], oyna), [days, oyna]);
	const engKatta = useMemo(
		() => series.reduce((eng, nuqta) => Math.max(eng, nuqta.lateMinutes), 0),
		[series]
	);

	if (isError) {
		return <Alert type="error" showIcon message={t("overviewTab.loadError")} />;
	}

	if (isLoading) {
		return <Skeleton active />;
	}

	return (
		<Row gutter={[16, 16]} align="top">
			<Col xs={24} lg={16} className="flex flex-col gap-4">
				<Row gutter={[16, 16]}>
					<Col xs={24} sm={8}>
						<Card>
							<Statistic
								title={t("overviewTab.attendanceRate")}
								value={korsatkich.rate}
								suffix="%"
								// ⚠️ Maxraj — `dam` va `uzr` dan boshqa kunlar; izoh
								// bo'lmasa foiz nimadan hisoblangani ko'rinmasdi.
							/>
							<div className="mt-1 text-xs text-[#8a929e]">
								{t("overviewTab.attendanceRateNote", { soni: korsatkich.workedDays })}
							</div>
						</Card>
					</Col>

					<Col xs={24} sm={8}>
						<Card>
							{/* ⚠️ `avgLateMin` `null` bo'lishi mumkin — hech kim kechikmagan.
						    `Statistic` `null` ni qabul QILMAYDI (tipi `valueType`), va
						    uni `0` ga aylantirish «o'rtacha kechikish nol daqiqa» degan
						    yolg'on da'vo bo'lardi. `—` esa «o'lchanadigan hodisa yo'q». */}
							<Statistic
								title={t("overviewTab.avgLate")}
								value={korsatkich.avgLateMin ?? "—"}
								suffix={korsatkich.avgLateMin === null ? undefined : "daq"}
							/>
							<div className="mt-1 text-xs text-[#8a929e]">
								{korsatkich.lateDays === 0
									? t("overviewTab.avgLateNoneNote")
									: t("overviewTab.avgLateNote", { soni: korsatkich.lateDays })}
							</div>
						</Card>
					</Col>

					<Col xs={24} sm={8}>
						<Card>
							<Statistic
								title={t("overviewTab.exits")}
								value={korsatkich.exits}
								suffix={t("overviewTab.exitsUnit")}
							/>
							{/* ⚠️ Oyna oralig'i bu yerdan LENTA kartasiga ko'chdi — u yerda
							    butun blok uchun bir marta ko'rinadi. Butunlay olib
							    tashlanmaydi: usiz «N marta chiqish» qaysi davr uchun
							    ekani ko'rinmasdi. */}
							<div className="mt-1 text-xs text-[#8a929e]">
								{korsatkich.totalOutsideMin === 0
									? t("overviewTab.exitsNoneNote")
									: t("overviewTab.exitsNote", { vaqt: soatDaqiqa(korsatkich.totalOutsideMin) })}
							</div>
						</Card>
					</Col>
				</Row>

				<Card
					title={t("overviewTab.windowTitle", { soni: oynaKun })}
					extra={
						<Segmented<WindowDays>
							options={WINDOW_OPTIONS.map((kun) => ({
								label: t("overviewTab.windowOption", { soni: kun }),
								value: kun,
							}))}
							value={oynaKun}
							onChange={setOynaKun}
						/>
					}
				>
					<div className="mb-2 text-xs text-[#8a929e]">
						{t("overviewTab.windowRange", { from, to })}
					</div>
					<DayStrip cells={cells} windowDays={oynaKun} />
					<DayStripLegend />
				</Card>

				<Card
					title={t("overviewTab.lateChartTitle")}
					// ⚠️ «eng kattasi 22 daq» — kanvasdagi izoh shakli, lekin RAQAM
					// hisoblanadi, qattiq kodlanmaydi.
					extra={
						<span className="text-xs text-[#8a929e]">
							{t("overviewTab.lateChartNote", { soni: engKatta })}
						</span>
					}
				>
					<LateChartSlot series={series} />
				</Card>

				<Card
					title={t("overviewTab.recentDaysTitle")}
					extra={
						// ⚠️ `search` FUNKSIYA shaklida: `/employees/$id` marshrutida
						// `validateSearch` yo'q, ya'ni obyekt literali qat'iy tipga
						// urilib typecheck'ni yiqitardi (`as never` esa taqiqlangan).
						// AYNI naqsh `EmployeeDetailPage` ning yorliq almashtirishida.
						<Link
							to="."
							search={(eski: Record<string, unknown>) => ({ ...eski, tab: "davomat" })}
							className="text-[#1c5cab] hover:underline"
						>
							{t("overviewTab.allLink")}
						</Link>
					}
				>
					<RecentDaysTable days={days ?? []} />
				</Card>
			</Col>

			<Col xs={24} lg={8} className="flex flex-col gap-4">
				<RiskCard employee={employee} />
				<EmployeeZonesCard employeeId={employeeId} />
				<RecentAbsencesCard employeeId={employeeId} />
			</Col>
		</Row>
	);
}
